import { GuestData } from '../types';
import { createBasicAuthHeader } from './auth-storage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Stats {
  totalGuests: number;
  totalSeatsAvailable: number;
  totalConfirmed: number;
  totalDeclined: number;
  totalPending: number;
  totalConfirmedSeats: number;
  confirmedGuests: number;
  declinedGuests: number;
  totalKidsSeatsAvailable: number;
  totalConfirmedKidsSeats: number;
}

/**
 * Test authentication credentials
 */
export async function testAuth(username: string, password: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: createBasicAuthHeader(username, password),
      },
    });

    return response.ok;
  } catch (error) {
    console.error('Error testing auth:', error);
    return false;
  }
}

/**
 * Get all guests (admin only)
 */
export async function getAllGuests(authHeader: string): Promise<GuestData[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/guests`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.guests || [];
  } catch (error) {
    console.error('Error fetching all guests:', error);
    throw error;
  }
}

/**
 * Get guest statistics (admin only)
 */
export async function getStats(authHeader: string): Promise<Stats> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
}

/**
 * Create a new guest (admin only)
 */
export async function createGuest(authHeader: string, guest: Partial<GuestData>): Promise<GuestData> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/guests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      body: JSON.stringify(guest),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.guest;
  } catch (error) {
    console.error('Error creating guest:', error);
    throw error;
  }
}

/**
 * Update an existing guest (admin only)
 */
export async function updateGuest(
  authHeader: string,
  code: string,
  updates: Partial<GuestData>
): Promise<GuestData> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/guests/${code}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.guest;
  } catch (error) {
    console.error('Error updating guest:', error);
    throw error;
  }
}

/**
 * Delete a guest (admin only)
 */
export async function deleteGuest(authHeader: string, code: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/guests/${code}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('Error deleting guest:', error);
    throw error;
  }
}

/**
 * Import guests from CSV file (admin only)
 */
export async function importGuests(
  authHeader: string,
  file: File,
  columnMapping: {
    code: string;
    seat: string;
    guest: string;
    attendanceStatus?: string;
    confirmedSeats?: string;
    confirmationDate?: string;
    kidsSeats?: string;
    confirmedKidsSeats?: string;
  }
): Promise<{ success: boolean; importedCount: number }> {
  try {
    // Convert file to base64
    const base64File = await fileToBase64(file);

    const response = await fetch(`${API_BASE_URL}/admin/import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      body: JSON.stringify({
        file: base64File,
        columnMapping,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error importing guests:', error);
    throw error;
  }
}

/**
 * Export all guests to CSV file (admin only)
 */
export async function exportGuests(authHeader: string): Promise<Blob> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/export`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    // Convert base64 to Blob
    const binaryString = atob(result.file);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], {
      type: 'text/csv',
    });
  } catch (error) {
    console.error('Error exporting guests:', error);
    throw error;
  }
}

/**
 * Helper function to convert File to base64
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
